import { CoatingType } from 'Interfaces/ReferenceInterfaces/CoatingTypeInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { coatingTypeScreenServices } from 'services/ReferenceScreenServices/coatingTypeServices';

export class CoatingTypeStore {
  coatingTypes: CoatingType[] = [];
  isLoading: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedCoatingType: CoatingType | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchCoatingTypes = async (page = 1, pageSize = 10000) => {
    this.isLoading = true;
    try {
      const response = await coatingTypeScreenServices.getAllCoatingTypes(page, pageSize);
      runInAction(() => {
        this.coatingTypes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching coating types');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createCoatingType = async (coatingTypeData: Partial<CoatingType>) => {
    this.isLoading = true;
    try {
      const response = await coatingTypeScreenServices.createCoatingType(coatingTypeData);
      if (response.status) {
        toast.success('Coating type created successfully');
        this.fetchCoatingTypes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to create coating type');
      }
    } catch (error) {
      toast.error('Error creating coating type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateCoatingType = async (id: number, coatingTypeData: Partial<CoatingType>) => {
    this.isLoading = true;
    try {
      const response = await coatingTypeScreenServices.updateCoatingType(id, coatingTypeData);
      if (response.status) {
        toast.success('Coating type updated successfully');
        this.fetchCoatingTypes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to update coating type');
      }
    } catch (error) {
      toast.error('Error updating coating type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteCoatingType = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await coatingTypeScreenServices.deleteCoatingType(id);
      if (response.status) {
        toast.success('Coating type deleted successfully');
        this.fetchCoatingTypes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to delete coating type');
      }
    } catch (error) {
      toast.error('Error deleting coating type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedCoatingType = (coatingType: CoatingType | null) => {
    this.selectedCoatingType = coatingType;
  };
}

export const coatingTypeStore = new CoatingTypeStore();
